<script setup>
import { capitalized, formatFloat } from "@/utilities/helpers";
import { typeEquipmentEnum } from "../core/FormData";
import util from "@/assets/js/util";
import { downloadReportESG } from "@/utilities/globalFunction";
import { StatusEnum, WorkAreaEnum } from "@/utilities/enum";
</script>
<template>
  <v-dialog width="1300" @click:outside="closeModal">
    <v-card class="rounded-lg h-auto !bg-white" :loading="loading">
      <div class="pt-3 bg-slate-100">
        <div class="flex justify-between items-center pb-2 px-3">
          <div>
            <h4 class="font-bold text-lg">ESG Summary</h4>
            <h5 class="font-medium text-xs italic text-gray-500">
              {{
                tabs
                  ? formData.month + " - " + formData.year
                  : formData.contractor_name +
                    " - " +
                    formData.month +
                    " - " +
                    formData.year
              }}
            </h5>
          </div>
          <div class="flex gap-3 items-center">
            <v-btn
              variant="outlined"
              class="capitalize text-xs w-28"
              :class="[
                formData.id && formData?.status !== StatusEnum.DRAFT
                  ? 'cursor-pointer bg-slate-100 border-green-600 text-green-600'
                  : 'cursor-not-allowed opacity-80 border-gray-400',
              ]"
              @click="
                formData.id && formData?.status !== StatusEnum.DRAFT
                  ? downloadExcel()
                  : ''
              "
            >
              <template #prepend>
                <img
                  :src="
                    require(`../../../assets/icon/${
                      formData.id && formData?.status !== StatusEnum.DRAFT
                        ? 'download-green.png'
                        : 'download.png'
                    }`)
                  "
                  alt="Download Icon"
                  class="h-4 w-auto"
                />
              </template>
              <span
                class="font-medium"
                :class="{
                  'opacity-60':
                    !formData.id && formData?.status === StatusEnum.DRAFT,
                }"
              >
                Export
              </span>
            </v-btn>
            <v-btn
              variant="outlined"
              class="capitalize text-xs w-28 border-gray-400 bg-slate-100"
              @click="closeModal"
            >
              <span class="text-gray-500"> Close </span>
            </v-btn>
          </div>
        </div>
        <div class="flex items-center overflow-y-hidden w-full flex-nowrap">
          <div
            v-for="(tab, index) in tabs"
            :key="index"
            class="h-8 flex items-center justify-center w-auto cursor-pointer"
            :class="[
              tab.active
                ? 'bg-white border-t-[2px] border-t-red-600 shadow-lg'
                : 'text-gray-400',
            ]"
            @click="getSummary(tab.id)"
          >
            <h5 class="text-sm px-7 font-medium">
              {{ tab.name }}
            </h5>
          </div>
        </div>
      </div>
      <div class="rounded-md mx-3 my-3 border !bg-white">
        <div
          class="flex items-center justify-between bg-slate-900 rounded-t-md px-3 py-2"
        >
          <div class="flex gap-2 items-center">
            <img
              src="../../../assets/icon/mining-production.png"
              alt="icon mining"
              class="w-7 h-auto"
            />
            <h5 class="text-white font-medium text-sm">
              {{
                formData?.area === WorkAreaEnum.MINING
                  ? "Mining Production"
                  : "Mitra " + capitalized(formData?.area)
              }}
            </h5>
          </div>
          <div class="flex gap-3 items-center">
            <h5 class="text-white font-medium text-sm">
              Q{{ formData.quartal }}
            </h5>
            <div
              class="rounded-full px-4 py-[6px]"
              :class="{
                'bg-green-100 text-green-500':
                  formData.status === StatusEnum.COMPLETED,
                'bg-orange-100 text-orange-400':
                  formData.status === StatusEnum.REVISION,
                'bg-yellow-100 text-orange-400':
                  formData.status === StatusEnum.WAITING_APPROVAL,
                'bg-gray-100 text-gray-400':
                  formData.status === StatusEnum.DRAFT,
              }"
            >
              <h5 class="text-nowrap font-semibold text-xs">
                {{
                  isPjo && formData.status === StatusEnum.WAITING_APPROVAL
                    ? "Need Approval"
                    : isContractor && formData.status === StatusEnum.COMPLETED
                    ? "Approved"
                    : capitalized(formData.status)
                }}
              </h5>
            </div>
          </div>
        </div>

        <div class="p-2">
          <div class="grid grid-cols-2 gap-1">
            <div
              class="border rounded-md h-auto p-2 items-center flex justify-between"
            >
              <h6 class="text-[10px] font-normal">OB Production <br />(BCM)</h6>
              <h5 class="text-sm font-medium">
                {{
                  formData?.production?.production_ob
                    ? formatFloat(formData.production.production_ob)
                    : "-"
                }}
              </h5>
            </div>
            <div
              class="border rounded-md h-auto p-2 items-center flex justify-between"
            >
              <h6 class="text-[10px] font-normal">
                Coal Production <br />(Ton)
              </h6>
              <h5 class="text-sm font-medium">
                {{
                  formData?.production?.production_coal
                    ? formatFloat(formData.production.production_coal)
                    : "-"
                }}
              </h5>
            </div>
          </div>
          <div
            class="grid gap-1 mt-2"
            :class="{
              'grid-cols-1': formData?.version === 1,
              'grid-cols-2': formData?.version === 2,
            }"
          >
            <div
              class="border rounded-md h-auto p-2 items-center flex justify-between"
              :class="{ 'bg-stone-100': formData?.version === 1 }"
            >
              <h6 class="text-[10px] font-normal">
                OB + Coal Production <br />(Ton)
              </h6>
              <h5 class="text-sm font-medium">
                {{
                  formData?.production?.production
                    ? formatFloat(formData.production.production)
                    : "-"
                }}
              </h5>
            </div>
            <div
              class="border rounded-md h-auto p-2 items-center flex justify-between"
              v-if="formData.version === 2"
            >
              <h6 class="text-[10px] font-normal">
                Emisi Fugitif<br />
                (TonCO2e)
              </h6>
              <h5 class="text-sm font-medium">
                {{
                  formData?.production?.emission_fugitif
                    ? formatFloat(formData.production.emission_fugitif)
                    : "-"
                }}
              </h5>
            </div>
          </div>
        </div>
      </div>
      <div class="rounded-md mx-3 mb-3 border">
        <div
          class="flex items-center justify-between bg-slate-900 rounded-t-md px-3 py-2"
        >
          <div class="flex gap-2 items-center">
            <img
              src="../../../assets/icon/elektrifikasi.png"
              alt="icon elektrifikasi"
              class="w-7 h-auto"
            />
            <h5 class="text-white font-medium text-sm">
              Consumption & Emission Summary
            </h5>
          </div>
          <div class="flex gap-3 items-center">
            <h5 class="text-white font-medium text-sm">
              Q{{ formData.quartal }}
            </h5>
            <div
              class="rounded-full px-4 py-[6px]"
              :class="{
                'bg-green-100 text-green-500':
                  formData.status === StatusEnum.COMPLETED,
                'bg-orange-100 text-orange-400':
                  formData.status === StatusEnum.REVISION,
                'bg-yellow-100 text-orange-400':
                  formData.status === StatusEnum.WAITING_APPROVAL,
                'bg-gray-100 text-gray-400':
                  formData.status === StatusEnum.DRAFT,
              }"
            >
              <h5 class="text-nowrap font-semibold text-xs">
                {{
                  isPjo && formData.status === StatusEnum.WAITING_APPROVAL
                    ? "Need Approval"
                    : isContractor && formData.status === StatusEnum.COMPLETED
                    ? "Approved"
                    : capitalized(formData.status)
                }}
              </h5>
            </div>
          </div>
        </div>
        <div class="p-2">
          <div
            class="grid gap-1"
            :class="{
              'grid-cols-10': formData.version === 1,
              'grid-cols-8': formData.version === 2,
            }"
          >
            <div
              v-for="consume in consumeEmissionSummary.filter((item) =>
                item.version.includes(formData?.version)
              )"
              :key="consume.id"
              class="rounded-md h-auto p-2 items-center flex justify-center cursor-pointer"
              :class="[
                consumeActive === consume.id
                  ? 'border-[1px] border-red-600 bg-red-100'
                  : 'border',
              ]"
              @click="
                () => {
                  consumeActive = consume.id;
                  scrollToTarget(consume.id);
                }
              "
            >
              <h5
                class="text-[11px] text-center"
                :class="[
                  consumeActive === consume.id ? 'font-medium' : 'font-normal',
                ]"
              >
                {{ consume.title }}
              </h5>
            </div>
          </div>
        </div>
        <div class="px-2 pb-2">
          <div class="rounded border overflow-x-auto" ref="scrollWrapper">
            <table>
              <tbody>
              <tr>
                <td rowspan="2" class="border-r border-b bg-stone-100">
                  <h6 class="text-xs font-medium p-2 text-nowrap">
                    Jenis Sumber Emisi Peralatan
                  </h6>
                </td>
                <td colspan="2" class="border-b border-r bg-orange-100" ref="1">
                  <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                    Hauling (Hanya diisi oleh Mitra Hauling)
                  </h6>
                </td>
                <td
                  colspan="2"
                  class="border-b border-r bg-sky-100 text-nowrap"
                  ref="2"
                >
                  <h6 class="text-xs font-medium p-2 text-center">
                    Konsumsi Bahan Bakar (Berdasarkan Jenis Bahan Bakar)
                  </h6>
                </td>
                <td
                  colspan="2"
                  class="border-b border-r bg-sky-100 text-nowrap"
                  ref="3"
                >
                  <h6 class="text-xs font-medium p-2 text-center">
                    Elektrifikasi
                  </h6>
                </td>
                <td
                  colspan="2"
                  class="border-b border-r bg-sky-100 text-nowrap"
                  ref="4"
                >
                  <h6 class="text-xs font-medium p-2 text-center">
                    Energi Terbarukan
                  </h6>
                </td>
                <td
                  colspan="3"
                  class="border-b border-r bg-red-100 text-nowrap"
                  ref="5"
                >
                  <h6 class="text-xs font-medium p-2 text-center">
                    Konsumsi Energi Berdasarkan Jenis Bahan Bakar (Scope 1)
                  </h6>
                </td>
                <template v-if="formData?.version === 1">
                  <td
                    colspan="4"
                    class="border-b border-r bg-red-100 text-nowrap"
                    ref="6"
                  >
                    <h6 class="text-xs font-medium p-2 text-center">
                      Beban Emisi Bahan Bakar (Biofuel)
                    </h6>
                  </td>
                  <td
                    colspan="4"
                    class="border-b border-r bg-red-100 text-nowrap"
                    ref="7"
                  >
                    <h6 class="text-xs font-medium p-2 text-center">
                      Beban Emisi Bahan Bakar BBM Fosil
                    </h6>
                  </td>
                  <td class="border-b border-r bg-red-100 text-nowrap"></td>
                  <td
                    colspan="4"
                    class="border-b border-r bg-red-100 text-nowrap"
                    ref="8"
                  >
                    <h6 class="text-xs font-medium p-2 text-center">
                      Emisi Bahan Bakar (Scope 1)
                    </h6>
                  </td>
                  <td
                    colspan="4"
                    class="border-b border-r bg-red-100 text-nowrap"
                  >
                    <h6 class="text-xs font-medium p-2 text-center">
                      Emisi Bahan Bakar (Scope 1)
                    </h6>
                  </td>
                  <td class="border-b border-r bg-red-100 text-nowrap"></td>
                  <td class="border-b border-r bg-red-100 text-nowrap"></td>
                  <td
                    class="border-b border-r bg-green-100 text-nowrap"
                    ref="9"
                  >
                    <h6 class="text-xs font-medium p-2 text-center">
                      Konsumsi Energi Elektrifikasi
                    </h6>
                  </td>
                  <td class="border-b border-r bg-green-100 text-nowrap">
                    <h6 class="text-xs font-medium p-2 text-center">
                      Konsumsi Energi Terbarukan
                    </h6>
                  </td>
                  <td
                    class="border-b border-r bg-green-100 text-nowrap"
                    ref="10"
                  >
                    <h6 class="text-xs font-medium p-2 text-center">
                      Emisi Elektrifikasi
                    </h6>
                  </td>
                  <td class="border-b bg-green-100 text-nowrap">
                    <h6 class="text-xs font-medium p-2 text-center">
                      Emisi Energi Terbarukan
                    </h6>
                  </td>
                </template>
                <template v-if="formData?.version === 2">
                  <td
                    colspan="3"
                    class="border-b border-r bg-red-100 text-nowrap"
                    ref="8"
                  >
                    <h6 class="text-xs font-medium p-2 text-center">
                      Emisi Bahan Bakar (Scope 1)
                    </h6>
                  </td>
                  <td class="border-b border-r bg-red-100 text-nowrap"></td>
                  <td class="border-b border-r bg-red-100 text-nowrap">
                    <h6 class="text-xs font-medium p-2 text-center">
                      Konsumsi Energi Elektrifikasi
                    </h6>
                  </td>
                  <td class="border-b border-r bg-red-100 text-nowrap">
                    <h6 class="text-xs font-medium p-2 text-center">
                      Konsumsi Energi Terbarukan
                    </h6>
                  </td>
                  <td
                    class="border-b border-r bg-yellow-100 text-nowrap"
                    ref="9"
                  >
                    <h6 class="text-xs font-medium p-2 text-center">
                      Emisi Elektrifikasi
                    </h6>
                  </td>
                  <td class="border-b border-r bg-yellow-100 text-nowrap">
                    <h6 class="text-xs font-medium p-2 text-center">
                      Emisi Energi Terbarukan
                    </h6>
                  </td>
                  <td
                    class="border-b border-r bg-red-100 text-nowrap"
                    ref="11"
                  ></td>
                  <td class="border-b border-r bg-red-100 text-nowrap"></td>
                </template>
              </tr>
              <tr>
                <td class="border-r border-b bg-orange-100">
                  <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                    Jumlah Unit
                  </h6>
                </td>
                <td class="border-r border-b bg-orange-100">
                  <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                    Detail Jenis Peralatan
                  </h6>
                </td>

                <td class="border-r border-b bg-sky-100">
                  <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                    Jenis Bahan Bakar
                  </h6>
                </td>
                <td class="border-r border-b bg-sky-100">
                  <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                    Konsumsi Bahan Bakar
                  </h6>
                </td>

                <td class="border-r border-b bg-sky-100">
                  <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                    Jenis Elektrifikasi
                  </h6>
                </td>
                <td class="border-r border-b bg-sky-100">
                  <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                    Konsumsi Elektrifikasi (MWh)
                  </h6>
                </td>

                <td class="border-r border-b bg-sky-100">
                  <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                    Jenis Energi Terbarukan
                  </h6>
                </td>
                <td class="border-r border-b bg-sky-100">
                  <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                    Konsumsi Energi Terbarukan (MWh)
                  </h6>
                </td>

                <td class="border-r border-b bg-red-100">
                  <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                    Konsumsi Energi Biofuel(Tj)
                  </h6>
                </td>
                <td class="border-r border-b bg-red-100">
                  <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                    Konsumsi Energi BBM Fosil (Tj)
                  </h6>
                </td>
                <td class="border-r border-b bg-red-100">
                  <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                    Total Konsumsi Energy (Tj)
                  </h6>
                </td>

                <template v-if="formData?.version === 1">
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi CO2 Biofuel (Ton)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi CH4 Biofuel (Ton)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi N2O Biofuel (Ton)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Total Beban Emisi Biofuel (Ton)
                    </h6>
                  </td>

                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi CO2 BBM Fosil (Ton)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi VH4 BBM Fosil (Ton)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi N2O BBM Fosil (Ton)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Total Beban Emisi BBM Fosil (Ton)
                    </h6>
                  </td>

                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Total Beban Emisi (Ton)
                    </h6>
                  </td>

                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi CO2 Biofuel (TonCO2e)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi CH4 Biofuel (TonCO2e)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi N2O Biofuel (TonCO2e)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Total Beban Emisi Biofuel (TonCO2e)
                    </h6>
                  </td>

                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi CO2 BBM Fosil (TonCO2e)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi CH4 BBM Fosil (TonCO2e)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi N2O BBM Fosil (TonCO2e)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Total Beban Emisi BBM Fosil (TonCO2e)
                    </h6>
                  </td>

                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Total Emisi (TonCO2e)
                    </h6>
                  </td>

                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Fuel Ratio (Ton/Liter)
                    </h6>
                  </td>

                  <td class="border-r border-b bg-green-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Konsumsi Energi Elektrifikasi(Tj)
                    </h6>
                  </td>

                  <td class="border-r border-b bg-green-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Konsumsi Energi Terbarukan(Tj)
                    </h6>
                  </td>

                  <td class="border-r border-b bg-green-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi Elektrifikasi (TonCO2e)
                    </h6>
                  </td>

                  <td class="border-b bg-green-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi Energi Terbarukan (TonCO2e)
                    </h6>
                  </td>
                </template>
                <template v-if="formData?.version === 2">
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi Biofuel (kgCO2e)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi Solar (kgCO2e)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Total Emisi (kgCO2e)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Fuel Ratio (Ton/Liter)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Konsumsi Energi Elektrifikasi (TJ)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Konsumsi Energi Terbarukan (TJ)
                    </h6>
                  </td>
                  <td class="border-r border-b bg-yellow-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi Elektrifikasi (TonCO2e)
                    </h6>
                  </td>

                  <td class="border-r border-b bg-yellow-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Emisi Energi Terbarukan (TonCO2e)
                    </h6>
                  </td>

                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Total Konsumsi Bahan Bakar Listrik (Liter B40)
                    </h6>
                  </td>

                  <td class="border-r border-b bg-red-100">
                    <h6 class="text-xs font-medium p-2 text-center text-nowrap">
                      Total Konsumsi Bahan Bakar ET (Liter)
                    </h6>
                  </td>
                </template>
              </tr>
              <tr v-for="(data, index) in formData.summary" :key="index">
                <td class="border-r">
                  <div class="border p-2 mx-2 mt-1 rounded">
                    <h6 class="text-[10px] font-normal text-nowrap">
                      {{
                        data?.fuel?.type_equipment ===
                        typeEquipmentEnum.BERGERAK
                          ? "Peralatan Sumber Emisi Bergerak"
                          : data?.fuel?.type_equipment ===
                            typeEquipmentEnum.TIDAK_BERGERAK
                          ? "Peralatan Sumber Emisi Tidak Bergerak"
                          : "-"
                      }}
                    </h6>
                  </div>
                </td>
                <td class="border-r">
                  <div class="border p-2 mx-2 mt-1 rounded">
                    <h6 class="text-[10px] font-normal text-nowrap">
                      {{ data?.equipment?.unit ? data.equipment.unit : "-" }}
                    </h6>
                  </div>
                </td>
                <td class="border-r">
                  <div class="border p-2 mx-2 mt-1 rounded">
                    <h6 class="text-[10px] font-normal text-nowrap">
                      {{ data?.equipment?.equipment || "-" }}
                    </h6>
                  </div>
                </td>
                <td class="border-r">
                  <div class="border p-2 mx-2 mt-1 rounded">
                    <h6 class="text-[10px] font-normal text-nowrap">
                      {{ data?.fuel?.fuel ? data.fuel.fuel : "-" }}
                    </h6>
                  </div>
                </td>
                <td class="border-r">
                  <div class="border p-2 mx-2 mt-1 rounded">
                    <h6 class="text-[10px] font-normal text-nowrap">
                      {{ data?.fuel?.volume ? data.fuel.volume : "-" }}
                    </h6>
                  </div>
                </td>
                <td class="border-r">
                  <div class="border p-2 mx-2 mt-1 rounded">
                    <h6 class="text-[10px] font-normal text-nowrap">
                      {{ data?.electric?.name ? data.electric.name : "-" }}
                    </h6>
                  </div>
                </td>
                <td class="border-r">
                  <div class="border p-2 mx-2 mt-1 rounded">
                    <h6 class="text-[10px] font-normal text-nowrap">
                      {{ data?.electric?.total ? data.electric.total : "-" }}
                    </h6>
                  </div>
                </td>
                <td class="border-r">
                  <div class="border p-2 mx-2 mt-1 rounded">
                    <h6 class="text-[10px] font-normal text-nowrap">
                      {{ data?.renewable?.name ? data.renewable.name : "-" }}
                    </h6>
                  </div>
                </td>
                <td class="border-r">
                  <div class="border p-2 mx-2 mt-1 rounded">
                    <h6 class="text-[10px] font-normal text-nowrap">
                      {{
                        data?.renewable?.consume ? data.renewable.consume : "-"
                      }}
                    </h6>
                  </div>
                </td>
                <td class="border-r">
                  <div class="border p-2 mx-2 mt-1 rounded">
                    <h6 class="text-[10px] font-normal text-nowrap">
                      {{
                        data?.fuel?.consume_energy_biofuel
                          ? formatFloat(data.fuel.consume_energy_biofuel)
                          : "-"
                      }}
                    </h6>
                  </div>
                </td>
                <td class="border-r">
                  <div class="border p-2 mx-2 mt-1 rounded">
                    <h6 class="text-[10px] font-normal text-nowrap">
                      {{
                        data?.fuel?.consume_energy_fossil
                          ? formatFloat(data.fuel.consume_energy_fossil)
                          : "-"
                      }}
                    </h6>
                  </div>
                </td>
                <td class="border-r">
                  <div class="border p-2 mx-2 mt-1 rounded">
                    <h6 class="text-[10px] font-normal text-nowrap">
                      {{
                        data?.fuel?.total_consume_energy
                          ? formatFloat(data.fuel.total_consume_energy)
                          : "-"
                      }}
                    </h6>
                  </div>
                </td>

                <template v-if="formData?.version === 1">
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.emission_biofuel_co2
                            ? formatFloat(data.fuel.emission_biofuel_co2)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.emission_biofuel_ch4
                            ? formatFloat(data.fuel.emission_biofuel_ch4)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.emission_biofuel_n2o
                            ? formatFloat(data.fuel.emission_biofuel_n2o)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.total_emission_biofuel
                            ? formatFloat(data.fuel.total_emission_biofuel)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.emission_fossil_co2
                            ? formatFloat(data.fuel.emission_fossil_co2)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.emission_fossil_ch4
                            ? formatFloat(data.fuel.emission_fossil_ch4)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.emission_fossil_n2o
                            ? formatFloat(data.fuel.emission_fossil_n2o)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.total_emission_fossil
                            ? formatFloat(data.fuel.total_emission_fossil)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.total_emission
                            ? formatFloat(data.fuel.total_emission)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.emission_biofuel_co2_ton_co2e
                            ? formatFloat(
                                data.fuel.emission_biofuel_co2_ton_co2e
                              )
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.emission_biofuel_ch4_ton_co2e
                            ? formatFloat(
                                data.fuel.emission_biofuel_ch4_ton_co2e
                              )
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.emission_biofuel_n2o_ton_co2e
                            ? formatFloat(
                                data.fuel.emission_biofuel_n2o_ton_co2e
                              )
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.total_emission_biofuel_ton_co2e
                            ? formatFloat(
                                data.fuel.total_emission_biofuel_ton_co2e
                              )
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.emission_fossil_co2_ton_co2e
                            ? formatFloat(
                                data.fuel.emission_fossil_co2_ton_co2e
                              )
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.emission_fossil_ch4_ton_co2e
                            ? formatFloat(
                                data.fuel.emission_fossil_ch4_ton_co2e
                              )
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.emission_fossil_n2o_ton_co2e
                            ? formatFloat(
                                data.fuel.emission_fossil_n2o_ton_co2e
                              )
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.total_emission_fossil_ton_co2e
                            ? formatFloat(
                                data.fuel.total_emission_fossil_ton_co2e
                              )
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.total_emission_ton_co2e
                            ? formatFloat(data.fuel.total_emission_ton_co2e)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.fuel_ratio
                            ? formatFloat(data.fuel.fuel_ratio)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.electric?.electrification
                            ? formatFloat(data.electric.electrification)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.renewable?.renewable_energy
                            ? formatFloat(data.renewable.renewable_energy)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.electric?.emission_electrification
                            ? formatFloat(
                                data.electric.emission_electrification
                              )
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.renewable?.emission_renewable_energy
                            ? formatFloat(
                                data.renewable.emission_renewable_energy
                              )
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                </template>
                <template v-if="formData?.version === 2">
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.emission_biofuel_kg_co2e
                            ? formatFloat(data.fuel.emission_biofuel_kg_co2e)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.emission_solar_kg_co2e
                            ? formatFloat(data.fuel.emission_solar_kg_co2e)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.total_emission_kg_co2e
                            ? formatFloat(data.fuel.total_emission_kg_co2e)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.fuel?.fuel_ratio
                            ? formatFloat(data.fuel.fuel_ratio)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.electric?.electrification
                            ? formatFloat(data.electric.electrification)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.renewable?.renewable_energy
                            ? formatFloat(data.renewable.renewable_energy)
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.electric?.emission_electrification
                            ? formatFloat(
                                data.electric.emission_electrification
                              )
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.renewable?.emission_renewable_energy
                            ? formatFloat(
                                data.renewable.emission_renewable_energy
                              )
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.electric?.consume_fuel_electric
                            ? formatFloat(
                                data.electric.consume_fuel_electric,
                                5
                              )
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                  <td class="border-r">
                    <div class="border p-2 mx-2 mt-1 rounded">
                      <h6 class="text-[10px] font-normal text-nowrap">
                        {{
                          data?.renewable?.consume_fuel_renewable_energy
                            ? formatFloat(
                                data.renewable.consume_fuel_renewable_energy,
                                6
                              )
                            : "-"
                        }}
                      </h6>
                    </div>
                  </td>
                </template>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "SummaryModal",
  data() {
    return {
      consumeActive: 1,
      consumeEmissionSummary: [
        {
          id: 1,
          title: "Hauling (Hanya diisi oleh Mitra Hauling)",
          version: [1, 2],
        },
        {
          id: 2,
          title: "Konsumsi Bahan Bakar (Berdasarkan jenis Bahan Bakar)",
          version: [1, 2],
        },
        {
          id: 3,
          title: "Elektrifikasi",
          version: [1, 2],
        },
        {
          id: 4,
          title: "Energi Terbarukan",
          version: [1, 2],
        },
        {
          id: 5,
          title: "Konsumsi Energi Berdasarkan Jenis Bahan Bakar (Scope 1)",
          version: [1, 2],
        },
        {
          id: 6,
          title: "Beban Emisi Bahan Bakar (Biofuel)",
          version: [1],
        },
        {
          id: 7,
          title: "Beban Emisi Bahan Bakar BBM Fosil",
          version: [1],
        },
        {
          id: 8,
          title: "Emisi Bahan Bakar (Scope 1)",
          version: [1, 2],
        },
        {
          id: 9,
          title: "Konsumsi Elektrifikasi dan Energi Terbarukan",
          version: [1, 2],
        },
        {
          id: 10,
          title: "Emisi Elektrifikasi dan Energi Terbarukan",
          version: [1],
        },
        {
          id: 11,
          title: "Total Konsumsi Bahan Bakar",
          version: [2],
        },
      ],
    };
  },
  props: {
    loading: Boolean,
    formData: Object,
    withSub: Boolean,
    tabs: Array,
    isPjo: Boolean,
    isContractor: Boolean,
    getSummary: Function,
  },
  methods: {
    closeModal() {
      this.$emit("close-modal");
    },
    scrollToTarget(target) {
      const targetEl = this.$refs[target];
      if (targetEl && targetEl.scrollIntoView) {
        targetEl.scrollIntoView({ behavior: "smooth", inline: "center" });
      }
    },
    downloadExcel() {
      try {
        let filename =
          this.formData.month +
          "_" +
          this.formData.year +
          "_" +
          this.formData.contractor_name;

        let bodyJson = {
          id: parseInt(this.formData.id),
          mode: this.withSub ? "COMBINED" : "SPARATE",
          is_pjo: this.isPjo ?? false,
          is_contractor: this.isContractor ?? false,
        };

        downloadReportESG(filename, bodyJson);
      } catch (err) {
        util.showError(err);
      }
    },
  },
};
</script>
